/**
 * 거래 문서함 — 관급내역서·정산서 보관본 (v2.230.0 · 2026-10-06 · 개발 요청 #16)
 *
 * 나라장터쇼핑몰 현황에서 [관급내역서]·[정산서] 를 만들 때마다 여기 한 줄씩 남는다.
 *   · ⬇ 엑셀  : 저장된 입력값으로 다시 만들어 내려받기(만들 때와 같은 파일)
 *   · ✎ 열기  : 같은 작성 창을 그 내용으로 연다 → [고쳐 저장] 하면 이 줄을 고친다(새 줄 아님)
 *   · 🔗 연동 : 영업건 연동·바꾸기·풀기 — 그 영업건 [기타 관련서류]에 붙는 사본도 함께 바뀐다
 *   · 🗑 삭제 : 연동을 푼 뒤에만(영업건 사본이 남는 일을 막는다)
 * 보는 사람 = 영업 이상(사용자 결정 2026-10-06 · 서버도 같은 규칙) — 탭 정의의 salesOnly.
 */

import { lazy, Suspense, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as g2bApi from '../../lib/g2b-api';
import type { GwangeupInitial } from '../../components/g2b/GwangeupModal';
import type { SettlementInitial } from '../../components/g2b/SettlementModal';
import { SalesPickerModal } from './SalesPickerModal';
import { SegmentedControl } from '../../components/ui/SegmentedControl';

const GwangeupModal = lazy(() =>
  import('../../components/g2b/GwangeupModal').then((m) => ({ default: m.GwangeupModal })),
);
const SettlementModal = lazy(() =>
  import('../../components/g2b/SettlementModal').then((m) => ({ default: m.SettlementModal })),
);

const fmtKrw = (n: number | null): string => (n === null ? '-' : Math.round(n).toLocaleString('ko-KR'));
const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

type Opened =
  | { kind: '관급내역서'; initial: GwangeupInitial }
  | { kind: '정산서'; initial: SettlementInitial };

export function G2bDocsTab() {
  const qc = useQueryClient();
  const [type, setType] = useState<g2bApi.G2bDocType | ''>('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [linkTarget, setLinkTarget] = useState<g2bApi.G2bDocRow | null>(null);

  const listQ = useQuery({
    queryKey: ['g2b', 'documents', type, q.trim()],
    queryFn: () => g2bApi.listG2bDocs({ type, q }),
    staleTime: 10_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['g2b', 'documents'] });

  async function run(id: number, fn: () => Promise<unknown>) {
    setBusyId(id);
    try { await fn(); } catch (e) { window.alert((e as Error).message); } finally { setBusyId(null); }
  }

  async function openDoc(r: g2bApi.G2bDocRow) {
    await run(r.id, async () => {
      const d = await g2bApi.getG2bDoc(r.id);
      const linkedSales = d.linkedSales;
      if (d.docType === '관급내역서') {
        setOpened({ kind: '관급내역서', initial: { docId: d.id, payload: d.payload as g2bApi.GwangeupPayload, linkedSales } });
      } else {
        setOpened({ kind: '정산서', initial: { docId: d.id, payload: d.payload as g2bApi.SettlementPayload, linkedSales } });
      }
    });
  }

  const rows = listQ.data ?? [];

  return (
    <div className="space-y-3">
      <div className="bg-white rounded shadow-sm border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <SegmentedControl
          ariaLabel="문서 종류"
          options={[
            { key: 'all', label: '전체' },
            { key: '관급내역서', label: '관급내역서' },
            { key: '정산서', label: '정산서' },
          ]}
          value={type || 'all'}
          onChange={(k) => setType(k === 'all' ? '' : k)}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍 수요처·제안업체·영업건 번호·건명"
          className="ui-input min-w-[16rem] flex-1 max-w-md"
        />
        <span className="text-xs text-slate-500 ml-auto">
          {listQ.isFetching ? '불러오는 중…' : `${rows.length}건`} · 새로 만들기는 나라장터쇼핑몰 현황에서
        </span>
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="w-full text-[13px] border-collapse min-w-[880px]">
          <thead className="bg-slate-100 text-xs text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left w-[84px]">종류</th>
              <th className="px-2 py-2 text-left">수요처 · 제안업체</th>
              <th className="px-2 py-2 text-right w-[110px]">합계(원)</th>
              <th className="px-2 py-2 text-left min-w-[14rem]">연동 영업건</th>
              <th className="px-2 py-2 text-left w-[86px]">작성</th>
              <th className="px-2 py-2 text-left w-[124px]">고친 때</th>
              <th className="px-2 py-2 text-center w-[240px]">작업</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isError && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-rose-600">불러오지 못했습니다 — {(listQ.error as Error).message}</td></tr>
            )}
            {!listQ.isError && rows.length === 0 && !listQ.isLoading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                보관된 문서가 없습니다 — 나라장터쇼핑몰 현황의 [관급내역서]·[정산서] 로 만들면 여기 남습니다.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${r.docType === '관급내역서' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-800'}`}>{r.docType}</span>
                </td>
                <td className="px-2 py-1.5" title={r.filename}>
                  <div className="font-medium text-slate-800">{r.title || <span className="text-slate-400">(이름 없음)</span>}</div>
                  <div className="text-xs text-slate-500 truncate max-w-[22rem]">{r.filename}</div>
                </td>
                <td className="px-2 py-1.5 text-right num">{fmtKrw(r.totalAmount)}</td>
                <td className="px-2 py-1.5 text-xs">
                  {r.linkedSalesId ? (
                    r.linkedSales ? (
                      <span title={`${r.linkedSales.endUser ?? ''} · ${r.linkedSales.proposalCompany ?? ''}`}>
                        🔗 <b>{r.linkedSalesId}</b> {r.linkedSales.projectName}
                      </span>
                    ) : (
                      <span className="text-rose-600">🔗 {r.linkedSalesId} (영업건 없음)</span>
                    )
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-2 py-1.5 text-xs">{r.createdByName ?? '-'}</td>
                <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.updatedAt)}</td>
                <td className="px-2 py-1.5 whitespace-nowrap text-center">
                  <div className="inline-flex gap-1">
                    <button type="button" disabled={busyId === r.id}
                      onClick={() => run(r.id, () => g2bApi.downloadG2bDoc(r.id, r.filename))}
                      className="btn btn-secondary btn-sm">⬇ 엑셀</button>
                    <button type="button" disabled={busyId === r.id} onClick={() => void openDoc(r)}
                      className="btn btn-secondary btn-sm">✎ 열기</button>
                    <button type="button" disabled={busyId === r.id} onClick={() => setLinkTarget(r)}
                      title={r.linkedSalesId ? `영업건 연동됨 (${r.linkedSalesId}) — 눌러서 바꾸거나 풀기` : '영업건에 연동 — 그 영업건 [기타 관련서류]에 사본이 붙습니다'}
                      className={`btn btn-sm ${r.linkedSalesId ? 'btn-success' : 'btn-secondary'}`}>
                      🔗 {r.linkedSalesId ? '연동 변경' : '연동'}
                    </button>
                    <button type="button" disabled={busyId === r.id || !!r.linkedSalesId}
                      title={r.linkedSalesId ? '영업건 연동을 먼저 풀어 주세요' : '이 보관본을 지웁니다'}
                      onClick={() => {
                        if (!window.confirm(`${r.docType} 「${r.title || r.filename}」 보관본을 지울까요?`)) return;
                        void run(r.id, async () => { await g2bApi.deleteG2bDoc(r.id); await refresh(); });
                      }}
                      className="btn btn-danger btn-sm">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SalesPickerModal
        open={linkTarget !== null}
        docTitle={linkTarget?.title ?? ''}
        currentSalesId={linkTarget?.linkedSalesId ?? null}
        onPick={async (salesId) => {
          if (!linkTarget) return;
          await run(linkTarget.id, async () => { await g2bApi.linkG2bDoc(linkTarget.id, salesId); await refresh(); });
          setLinkTarget(null);
        }}
        onUnlink={linkTarget?.linkedSalesId ? async () => {
          if (!linkTarget) return;
          await run(linkTarget.id, async () => { await g2bApi.linkG2bDoc(linkTarget.id, null); await refresh(); });
          setLinkTarget(null);
        } : undefined}
        onClose={() => setLinkTarget(null)}
      />

      <Suspense fallback={null}>
        {opened?.kind === '관급내역서' && (
          <GwangeupModal initial={opened.initial} onSaved={() => void refresh()} onClose={() => setOpened(null)} />
        )}
        {opened?.kind === '정산서' && (
          <SettlementModal initial={opened.initial} onSaved={() => void refresh()} onClose={() => setOpened(null)} />
        )}
      </Suspense>
    </div>
  );
}
